import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Location bilgileri için interface'ler
 */
export interface LocationData {
  supplierId: string | null;
  country: string;
  city: string;
  airport: string;
  terminal: string | null;
  locationCode: string;
}

export interface LocationOption {
  value: string;
  label: string;
}

export interface LocationHierarchy {
  countries: LocationOption[];
  cities: Record<string, LocationOption[]>;
  airports: Record<string, LocationOption[]>;
  terminals: Record<string, LocationOption[]>;
  locationCodes: Record<string, LocationOption[]>;
}

/**
 * Locations Store State
 */
interface LocationsState {
  // Veri durumu
  rawData: LocationData[];
  hierarchy: LocationHierarchy;
  isLoading: boolean;
  error: string | null;
  lastFetchTime: number | null;
  
  // Cache süresi (5 dakika)
  cacheExpiry: number;
  
  // Actions
  setRawData: (data: LocationData[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearData: () => void;
  refreshData: () => void;
  
  // Hierarchy getters
  getCountries: () => LocationOption[];
  getCitiesByCountry: (country: string) => LocationOption[];
  getAirportsByCity: (city: string) => LocationOption[];
  getTerminalsByAirport: (airport: string) => LocationOption[];
  getAllLocationCodes: () => LocationOption[];
  getLocationDetailsByCode: (locationCode: string) => LocationData | null;

  // Cache kontrol
  isCacheValid: () => boolean;
}

/**
 * Başlangıç durumu
 */
const initialState = {
  rawData: [],
  hierarchy: {
    countries: [],
    cities: {},
    airports: {},
    terminals: {},
    locationCodes: {},
  },
  isLoading: false,
  error: null,
  lastFetchTime: null,
  cacheExpiry: 5 * 60 * 1000, // 5 dakika
};

// Global loading kontrolü için
let isGloballyLoading = false;

/**
 * Raw data'dan hierarchy oluşturur
 */
const buildHierarchy = (data: LocationData[]): LocationHierarchy => {
  const hierarchy: LocationHierarchy = {
    countries: [],
    cities: {},
    airports: {},
    terminals: {},
    locationCodes: {},
  };

  // Unique değerleri topla
  const uniqueCountries = new Set<string>();
  const citiesByCountry = new Map<string, Set<string>>();
  const airportsByCity = new Map<string, Set<string>>();
  const terminalsByAirport = new Map<string, Set<string>>();
  const uniqueLocationCodes = new Set<string>();

  data.forEach((item) => {
    const { country, city, airport, terminal, locationCode } = item;

    // Null/undefined kontrolü - daha esnek yapalım
    if (!country || !city || !airport || !locationCode) {
      return;
    }

    // Countries
    uniqueCountries.add(country);

    // Cities by Country
    if (!citiesByCountry.has(country)) {
      citiesByCountry.set(country, new Set());
    }
    citiesByCountry.get(country)!.add(city);

    // Airports by City
    if (!airportsByCity.has(city)) {
      airportsByCity.set(city, new Set());
    }
    airportsByCity.get(city)!.add(airport);

    // Terminals by Airport - terminal null olabilir
    if (terminal) {
      if (!terminalsByAirport.has(airport)) {
        terminalsByAirport.set(airport, new Set());
      }
      terminalsByAirport.get(airport)!.add(terminal);
    }

    // Location Codes - Bağımsız olarak topla
    uniqueLocationCodes.add(locationCode);
  });

  // Countries
  hierarchy.countries = Array.from(uniqueCountries)
    .sort()
    .map(country => ({ value: country, label: country }));

  // Cities by Country
  citiesByCountry.forEach((cities, country) => {
    hierarchy.cities[country] = Array.from(cities)
      .sort()
      .map(city => ({ value: city, label: city }));
  });

  // Airports by City
  airportsByCity.forEach((airports, city) => {
    hierarchy.airports[city] = Array.from(airports)
      .sort()
      .map(airport => ({ value: airport, label: airport }));
  });

  // Terminals by Airport
  terminalsByAirport.forEach((terminals, airport) => {
    hierarchy.terminals[airport] = Array.from(terminals)
      .sort()
      .map(terminal => ({ value: terminal, label: terminal }));
  });

  // Location Codes - Bağımsız liste
  hierarchy.locationCodes['all'] = Array.from(uniqueLocationCodes)
    .sort()
    .map(locationCode => ({ value: locationCode, label: locationCode }));

  return hierarchy;
};

/**
 * Locations Zustand Store
 */
export const useLocationsStore = create<LocationsState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Raw data setter - hierarchy'yi otomatik oluşturur
      setRawData: (data: LocationData[]) => {
        const hierarchy = buildHierarchy(data);
        set({
          rawData: data,
          hierarchy,
          error: null,
          lastFetchTime: Date.now(),
          isLoading: false, // Loading'i false yap
        });
      },

      setLoading: (isLoading: boolean) => {
        const currentState = get();
        // Eğer aynı loading durumu ise güncelleme yapma
        if (currentState.isLoading === isLoading) {
          return;
        }

        // Global loading kontrolü
        if (isLoading) {
          if (isGloballyLoading) {
            return; // Zaten global olarak yükleme yapılıyor
          }
          isGloballyLoading = true;
        } else {
          isGloballyLoading = false;
        }

        set({ isLoading });
      },

      setError: (error: string | null) => set({ error }),

      clearData: () => set({
        ...initialState,
        lastFetchTime: null,
      }),

      refreshData: () => {
        set({
          lastFetchTime: null,
          isLoading: false, // Loading'i sıfırla
        });
      },

      // Hierarchy getters
      getCountries: () => {
        const { hierarchy } = get();
        return hierarchy.countries;
      },

      getCitiesByCountry: (country: string) => {
        const { hierarchy } = get();
        return hierarchy.cities[country] || [];
      },

      getAirportsByCity: (city: string) => {
        const { hierarchy } = get();
        return hierarchy.airports[city] || [];
      },

      getTerminalsByAirport: (airport: string) => {
        const { hierarchy } = get();
        return hierarchy.terminals[airport] || [];
      },

      getAllLocationCodes: () => {
        const { hierarchy } = get();
        // Tüm location code'ları döndür
        return hierarchy.locationCodes['all'] || [];
      },

      getLocationDetailsByCode: (locationCode: string) => {
        const { rawData } = get();
        // Raw data'dan location code'a göre arama yap
        const found = rawData.find(item => item.locationCode === locationCode);
        return found || null;
      },

      // Cache kontrol
      isCacheValid: () => {
        const { lastFetchTime, cacheExpiry } = get();
        if (!lastFetchTime) return false;
        return Date.now() - lastFetchTime < cacheExpiry;
      },
    }),
    {
      name: 'locations-storage',
      partialize: (state) => ({
        rawData: state.rawData,
        hierarchy: state.hierarchy,
        lastFetchTime: state.lastFetchTime,
      }),
    }
  )
);
